import { FC } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Circle,
  Flex,
  Grid,
  HStack,
  Spacer,
  VStack,
} from '@chakra-ui/react';

import SunshineLoader from 'components/_core/SunshineLoader';
import {
  formatPeriodDate,
  getRatesAmount,
} from 'features/Accounting/pages/Estimation/common/utils';
import { ESTIMATION_REVENUE_EDIT } from 'features/Accounting/pages/Estimation/flows/microEstimation/components/MicroEstimationFlow';
import { useTaxForecastPeriod } from 'features/Accounting/pages/Estimation/hooks/useTaxForecastPeriod';

import { formatAmount } from '../../../../../../../../common/formatAmount';
import Accordion from '../../../../../../../../components/_core/Accordion';
import Button from '../../../../../../../../components/_core/Button';
import SunshineCard from '../../../../../../../../components/_core/SunshineCard';
import SunshineIcon from '../../../../../../../../components/_core/SunshineIcon';
import Text from '../../../../../../../../components/_core/Text';
import * as routes from '../../../../../../routes';
import locales from '../../../../locales';
import EstimationDetails from '../../../EstimationDetails';
import RevenueTooltip from '../../../RevenueTooltip';
import TaxStatus from '../../../TaxStatus';

interface CurrentEstimationOverviewProps {
  taxForecastPeriodId: string;
  isSubjectToVAT: boolean;
}

const CurrentEstimationOverview: FC<CurrentEstimationOverviewProps> = ({
  isSubjectToVAT,
  taxForecastPeriodId,
}) => {
  const { loading, taxForecastPeriod, taxForecastRates } =
    useTaxForecastPeriod(taxForecastPeriodId);

  if (loading || !taxForecastPeriod || !taxForecastRates?.length) {
    return <SunshineLoader />;
  }

  const ratesAmount = getRatesAmount(taxForecastRates);

  const totalRevenue = taxForecastRates[0]?.computedRevenue as number;
  const cotisationsAmount =
    ratesAmount.CFP + ratesAmount.FRAIS_CHAMBRE + ratesAmount.RSI;
  const taxAmount = ratesAmount.PRELEV_LIB;
  const netIncome = totalRevenue - cotisationsAmount - taxAmount;

  const getAmountPercentage = (amount: number) => {
    if (totalRevenue === 0) {
      return '0%';
    }

    const total = cotisationsAmount + taxAmount + netIncome;

    return `${((amount / total) * 100).toFixed(1)}%`;
  };

  const cotisationsPercentage = getAmountPercentage(cotisationsAmount);
  const taxPercentage = getAmountPercentage(taxAmount);
  const netIncomePercentage = getAmountPercentage(netIncome);

  return (
    <>
      <Text fontWeight="weight-500" size="lg" variant="primary">
        {locales.overview.title}
      </Text>

      <HStack align="center" mt="space-24">
        <Text fontWeight="weight-400" variant="primary">
          {formatPeriodDate(
            taxForecastPeriod.periodStartsAt,
            taxForecastPeriod.periodEndsAt,
          )}
        </Text>

        <Box marginLeft="space-8">
          <TaxStatus
            isPaid={taxForecastPeriod.isPaid}
            payBefore={taxForecastPeriod.payBefore}
          />
        </Box>
        <Spacer />
        <Button
          as={Link}
          icon="settings"
          to={routes.estimationSettings}
          variant="discreet"
        >
          {locales.overview.settings}
        </Button>
      </HStack>

      <SunshineCard mt="space-24" p={0}>
        <HStack
          borderBottomColor="grey.300"
          borderBottomWidth="1px"
          justifyContent="space-between"
          p="space-16"
        >
          <VStack align="start">
            <Text size="sm" variant="primary">
              {locales.overview.revenue}
            </Text>

            <Flex alignItems="center">
              <Text
                display="inline-block"
                fontWeight="weight-500"
                size="lg"
                variant="primary"
              >
                {formatAmount({ forcePlusSign: false, value: totalRevenue })}
              </Text>

              {isSubjectToVAT ? (
                <Text
                  display="inline-block"
                  fontWeight="weight-500"
                  marginLeft="space-4"
                  variant="primary"
                >
                  {locales.overview.withoutTax}
                </Text>
              ) : null}

              <RevenueTooltip
                complementaryRevenue={
                  taxForecastRates[0]?.complementaryRevenue ?? 0
                }
                isSubjectToVAT={isSubjectToVAT}
                shineRevenue={taxForecastRates[0]?.revenue ?? 0}
                vatAmount={taxForecastRates[0]?.vatAmount ?? 0}
              >
                <SunshineIcon
                  color="grey.600"
                  marginLeft="space-8"
                  name="information"
                  size="icon-20"
                />
              </RevenueTooltip>
            </Flex>
          </VStack>
          <Button
            as={Link}
            icon="edit"
            iconPosition="left"
            to={`${routes.microEstimationFlow}?${ESTIMATION_REVENUE_EDIT}=true`}
            variant="primary"
          >
            {locales.overview.edit}
          </Button>
        </HStack>

        <HStack gap={2} height="6px" margin="space-16">
          <Box
            backgroundColor="blue.500"
            borderRadius="radius-4"
            height="full"
            width={cotisationsPercentage}
          />
          <Box
            backgroundColor="grey.500"
            borderRadius="radius-4"
            height="full"
            width={taxPercentage}
          />
          <Box
            backgroundColor="grey.400"
            borderRadius="radius-4"
            height="full"
            width={netIncomePercentage}
          />
        </HStack>

        <Flex px="space-16">
          <Grid alignItems="center" flex={1} gap={8} templateColumns="auto 1fr">
            <Circle alignSelf="start" bg="blue.500" mt="space-4" size="10px" />
            <Box>
              <Text size="sm" variant="primary">
                {locales.overview.socialContributions}
              </Text>
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {formatAmount({
                  forcePlusSign: false,
                  value: cotisationsAmount,
                })}
              </Text>
              <Text size="sm" variant="light">
                {cotisationsPercentage}
              </Text>
            </Box>
          </Grid>
          <Box
            backgroundColor="grey.300"
            height="66px"
            mx="space-16"
            width="1px"
          />
          <Grid alignItems="center" flex={1} gap={8} templateColumns="auto 1fr">
            <Circle alignSelf="start" bg="grey.500" mt="space-4" size="10px" />
            <Box>
              <Text size="sm" variant="primary">
                {locales.overview.tax}
              </Text>
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {formatAmount({ forcePlusSign: false, value: taxAmount })}
              </Text>
              <Text size="sm" variant="light">
                {taxPercentage}
              </Text>
            </Box>
          </Grid>
          <Box
            backgroundColor="grey.300"
            height="66px"
            mx="space-16"
            width="1px"
          />
          <Grid alignItems="center" flex={1} gap={8} templateColumns="auto 1fr">
            <Circle alignSelf="start" bg="grey.400" mt="space-4" size="10px" />
            <Box>
              <Text size="sm" variant="primary">
                {locales.overview.netIncome}
              </Text>
              <Text fontWeight="weight-500" size="lg" variant="primary">
                {formatAmount({ forcePlusSign: false, value: netIncome })}
              </Text>
              <Text size="sm" variant="light">
                {netIncomePercentage}
              </Text>
            </Box>
          </Grid>
        </Flex>

        <Accordion marginTop="space-16">
          <EstimationDetails
            isCurrentPeriod={true}
            isOverview={true}
            taxForecastPeriodId={taxForecastPeriodId}
          />
        </Accordion>
      </SunshineCard>
    </>
  );
};

export default CurrentEstimationOverview;
